import React, { useState } from 'react';

import { getParticipantProgramJourney } from '../../../../api/v2/program_insights';
import useProgramInsightsMetric from '../use_program_insights_metric';
import MetricCard from '../metric_card';
import styles from './index.module.css';

const DESCRIPTION =
  'Participant progression through the PatientPartner program statuses in order, with the count that ' +
  'reached each stage and the drop-off between consecutive stages. Statuses with a zero count are ' +
  'still shown. Pre-consent and Post-consent split the funnel by when the participant first ' +
  'messaged a mentor; retention-only studies show the total only.';

// Chevron fill colors in stage order, mirroring the dashboard mockup.
const STAGE_COLORS = [
  'var(--pp-deep-horizon)',
  'var(--pp-teal-dark)',
  // Darkened so white counts and the Active / Not Active breakout stay readable.
  '#2f7780',
  '#316d80',
  'var(--pp-slate-blue)',
  '#3f6b86',
];

// view -> the response field each stage/drop-off value is read from.
const VIEW_FIELD = {
  total: 'total',
  pre: 'pre_consent',
  post: 'post_consent',
};

const VIEWS = [
  { id: 'total', label: 'Total' },
  { id: 'pre', label: 'Pre-consent' },
  { id: 'post', label: 'Post-consent' },
];

const formatCount = (value) => (value == null ? '—' : value.toLocaleString());

// Drop-off is previous stage minus this stage. A loss uses one hyphen. When the next stage is
// larger the value is negative, so show that increase with a plus. The sign is applied to the
// absolute count so a negative value is never printed as two minuses.
const formatDropOff = (value) => {
  if (value == null) return '—';
  if (value === 0) return '0';

  const amount = Math.abs(Number(value)).toLocaleString();
  return Number(value) > 0 ? `-${amount}` : `+${amount}`;
};

export default function ParticipantProgramJourney({ filters }) {
  const { data, status } = useProgramInsightsMetric(getParticipantProgramJourney, filters);
  const [view, setView] = useState('total');

  const split = data && data.recruitment_and_retention;
  // Retention-only studies only expose the total funnel, so force that view.
  const activeView = split ? view : 'total';
  const field = VIEW_FIELD[activeView];

  const hasData = data && data.stages[0] && data.stages[0].total > 0;

  return (
    <MetricCard
      title='Participant Program Journey'
      subtitle='First mentor message before or after study consent.'
      description={DESCRIPTION}
    >
      {status === 'loading' && <p className={styles.state}>Loading…</p>}
      {status === 'error' && <p className={styles.stateError}>We could not load this metric. Try again shortly.</p>}

      {status === 'loaded' && !hasData && (
        <p className={styles.state}>No participants match the current filters yet.</p>
      )}

      {status === 'loaded' && hasData && (
        <>
          {split && (
            <div className={styles.toggle} role='group' aria-label='Journey view'>
              {VIEWS.map((option) => (
                <button
                  key={option.id}
                  type='button'
                  aria-pressed={activeView === option.id}
                  className={`${styles.toggleButton} ${activeView === option.id ? styles.toggleButtonActive : ''}`}
                  onClick={() => setView(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <div className={styles.pipeline}>
            {data.stages.map((stage, index) => {
              const isLast = index === data.stages.length - 1;
              const nextDrop = isLast ? null : data.stages[index + 1].drop_off[field];

              return (
                <div key={stage.key} className={styles.stage}>
                  <div className={styles.stageLabel}>{stage.label}</div>
                  <div className={styles.chevronWrap}>
                    <div
                      className={`${styles.chevron} ${isLast ? styles.chevronLast : ''}`}
                      style={{ backgroundColor: STAGE_COLORS[index % STAGE_COLORS.length] }}
                    >
                      {stage.breakout ? (
                        <div className={styles.breakout}>
                          <div className={styles.breakoutRow}>
                            <span className={styles.breakoutLabel}>Active</span>
                            <span className={styles.breakoutCount}>{formatCount(stage.breakout.active[field])}</span>
                          </div>
                          <div className={styles.breakoutRow}>
                            <span className={styles.breakoutLabel}>Not Active</span>
                            <span className={styles.breakoutCount}>
                              {formatCount(stage.breakout.not_active[field])}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className={styles.stageCount}>{formatCount(stage[field])}</span>
                      )}
                    </div>

                    {!isLast && <span className={styles.dropOff}>{formatDropOff(nextDrop)}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          <p className={styles.footnote}>
            Drop-off between consecutive stages is shown on each connector. Statuses with a zero count are still
            displayed.
          </p>
        </>
      )}
    </MetricCard>
  );
}
